import { useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { ClipboardDocumentIcon, CheckIcon } from '@heroicons/react/24/outline';
import { useProject } from '../../contexts/ProjectContext';
import { dashboardService } from '../../services/dashboard';

export default function CreateKeyModal() {
  const navigate = useNavigate();
  const { reloadKeys } = useOutletContext() || {};
  const { projects, selectedHash } = useProject();
  const [name, setName] = useState('');
  const [project, setProject] = useState(selectedHash || projects?.[0]?.hash || '');
  const [saving, setSaving] = useState(false);
  const [createdToken, setCreatedToken] = useState('');
  const [copied, setCopied] = useState(false);

  const close = () => navigate('/settings/keys/');

  const copyToken = async () => {
    try {
      await navigator.clipboard.writeText(createdToken);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable */
    }
  };

  const submit = () => {
    setSaving(true);
    dashboardService.createKey(
      { name: name.trim() || 'default', project },
      (res) => {
        setCreatedToken(res.data.token);
        reloadKeys?.();
        setSaving(false);
      },
      () => setSaving(false),
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-2xl bg-theme-main border border-theme-secondary p-6 shadow-lg">
        {createdToken ? (
          <>
            <h2 className="text-lg font-medium mb-2">API key created</h2>
            <p className="text-sm opacity-70 mb-4">
              Copy this key now. You will not be able to see the full key again.
            </p>
            <div className="flex items-center gap-2 mb-6">
              <code className="flex-1 text-xs sm:text-sm font-mono break-all bg-theme-secondary/30 rounded-lg px-3 py-2 border border-theme-secondary">
                {createdToken}
              </code>
              <button
                type="button"
                onClick={copyToken}
                className="shrink-0 p-2 rounded-lg border border-theme-secondary hover:bg-theme-secondary/30"
              >
                {copied ? <CheckIcon className="h-4 w-4" /> : <ClipboardDocumentIcon className="h-4 w-4" />}
              </button>
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={close}
                className="px-4 py-2 rounded-lg bg-theme-primary text-theme-button text-sm font-medium"
              >
                Done
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 className="text-lg font-medium mb-4">Create API key</h2>
            <label className="block text-sm mb-1 opacity-70">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. production"
              autoFocus
              className="w-full rounded-lg border border-theme-secondary bg-theme-main px-3 py-2 text-sm mb-4"
            />
            <label className="block text-sm mb-1 opacity-70">Project</label>
            <select
              value={project}
              onChange={(e) => setProject(e.target.value)}
              className="w-full rounded-lg border border-theme-secondary bg-theme-main px-3 py-2 text-sm mb-6"
            >
              {projects.map((p) => (
                <option key={p.hash} value={p.hash}>{p.name}</option>
              ))}
            </select>
            <div className="flex justify-end gap-3">
              <button type="button" onClick={close} className="px-4 py-2 rounded-lg border border-theme-secondary text-sm">
                Cancel
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={saving}
                className="px-4 py-2 rounded-lg bg-theme-primary text-theme-button text-sm font-medium disabled:opacity-40"
              >
                {saving ? 'Creating…' : 'Create key'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
